import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const purchaseTickets = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      eventId: z.string().uuid(),
      items: z.array(z.object({
        ticketTypeId: z.string().uuid(),
        quantity: z.number().int().min(1).max(10),
      })).min(1).max(10),
    })
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getRequestHeader, getRequest } = await import("@tanstack/react-start/server");

    // Try multiple ways to get the auth token
    let token: string | undefined;
    
    const authHeader = getRequestHeader("authorization");
    if (authHeader) {
      token = authHeader.replace("Bearer ", "");
    }
    
    if (!token) {
      // Try to get from cookie
      const cookieHeader = getRequestHeader("cookie") || "";
      const cookies = Object.fromEntries(
        cookieHeader.split(";").map((c) => {
          const [key, ...val] = c.trim().split("=");
          return [key, val.join("=")];
        })
      );
      // Supabase stores tokens in cookies with project ref
      const projectRef = (process.env.SUPABASE_URL || "").match(/\/\/([^.]+)/)?.[1] || "";
      token = cookies[`sb-${projectRef}-auth-token`];
      if (token) {
        try {
          const parsed = JSON.parse(decodeURIComponent(token));
          token = parsed.access_token || parsed[0];
        } catch {
          // token might be the raw access token
        }
      }
    }

    if (!token) throw new Error("No autorizado. Por favor iniciá sesión.");

    const { createClient } = await import("@supabase/supabase-js");
    const supabaseUrl = process.env.SUPABASE_URL!;
    const supabaseKey = process.env.SUPABASE_PUBLISHABLE_KEY!;
    const userClient = createClient(supabaseUrl, supabaseKey, {
      global: { headers: { Authorization: `Bearer ${token}` } },
      auth: { persistSession: false },
    });

    const { data: userData } = await userClient.auth.getUser();
    if (!userData?.user) throw new Error("No autorizado. Por favor iniciá sesión.");
    const userId = userData.user.id;

    // Validate event exists
    const { data: event } = await supabaseAdmin
      .from("events")
      .select("id, max_per_user, title, organizer_id")
      .eq("id", data.eventId)
      .single();
    if (!event) throw new Error("Evento no encontrado");

    // Check total quantity
    const totalQty = data.items.reduce((sum, i) => sum + i.quantity, 0);
    if (totalQty > event.max_per_user) {
      throw new Error(`Máximo ${event.max_per_user} entradas por persona`);
    }

    // Check existing tickets for this user+event
    const { count: existingCount } = await supabaseAdmin
      .from("tickets")
      .select("*", { count: "exact", head: true })
      .eq("event_id", data.eventId)
      .eq("user_id", userId)
      .in("status", ["active", "used"]);

    if ((existingCount || 0) + totalQty > event.max_per_user) {
      throw new Error(`Ya tenés ${existingCount} entrada(s). Máximo ${event.max_per_user} por persona.`);
    }

    const ticketInserts: Array<{
      event_id: string;
      ticket_type_id: string;
      user_id: string;
      price: number;
      subtotal: number;
      fee_amount: number;
      qr_data: string;
      status: string;
    }> = [];
    const transactionInserts: Array<{
      ticket_id: string;
      buyer_id: string;
      price: number;
      type: string;
    }> = [];

    for (const item of data.items) {
      // Verify availability
      const { data: tt } = await supabaseAdmin
        .from("ticket_types")
        .select("id, price, available")
        .eq("id", item.ticketTypeId)
        .eq("event_id", data.eventId)
        .single();

      if (!tt) throw new Error("Tipo de ticket no encontrado");
      if (tt.available < item.quantity) throw new Error(`Solo quedan ${tt.available} entradas de este tipo`);

      // Decrement available
      await supabaseAdmin
        .from("ticket_types")
        .update({ available: tt.available - item.quantity })
        .eq("id", item.ticketTypeId);

      for (let i = 0; i < item.quantity; i++) {
        const ticketId = crypto.randomUUID();
        const qrData = `ZYNTRA-${ticketId}`;
        ticketInserts.push({
          event_id: data.eventId,
          ticket_type_id: item.ticketTypeId,
          user_id: userId,
          price: tt.price,
          subtotal: tt.price,
          fee_amount: 0,
          qr_data: qrData,
          status: "active",
        });
      }
    }

    // Insert tickets
    const { data: insertedTickets, error: ticketsError } = await supabaseAdmin
      .from("tickets")
      .insert(ticketInserts)
      .select("id, price");

    if (ticketsError) throw new Error(`Error al crear tickets: ${ticketsError.message}`);

    // Insert transactions
    if (insertedTickets) {
      for (const t of insertedTickets) {
        transactionInserts.push({
          ticket_id: t.id,
          buyer_id: userId,
          price: t.price,
          type: "purchase",
        });
      }
      await supabaseAdmin.from("transactions").insert(transactionInserts);
    }

    // Notify organizer
    const totalRevenue = ticketInserts.reduce((sum, t) => sum + t.price, 0);
    if (event.organizer_id && event.organizer_id !== userId) {
      await supabaseAdmin.from("notifications").insert({
        user_id: event.organizer_id,
        title: "¡Nueva venta!",
        message: `Se vendieron ${ticketInserts.length} entrada(s) para "${event.title}" por ₡${totalRevenue.toLocaleString("es-CR")}`,
        type: "sale",
        related_event_id: data.eventId,
      });
    }

    // Notify buyer
    await supabaseAdmin.from("notifications").insert({
      user_id: userId,
      title: "Compra confirmada",
      message: `Compraste ${ticketInserts.length} entrada(s) para "${event.title}"`,
      type: "purchase",
      related_event_id: data.eventId,
    });

    return { success: true, ticketCount: ticketInserts.length };
  });


/**
 * Resolve the operative role of the validating user for a given event.
 * Priority: admin → organizer → staff (scanner/manager) → null.
 */
async function resolveValidatorRole(
  supabaseAdmin: any,
  userId: string,
  eventId: string | null
): Promise<"admin" | "organizer" | "scanner" | "manager" | null> {
  const { data: isAdmin } = await supabaseAdmin.rpc("has_role", {
    _user_id: userId,
    _role: "admin",
  });
  if (isAdmin) return "admin";

  if (!eventId) return null;

  const { data: ev } = await supabaseAdmin
    .from("events")
    .select("organizer_id")
    .eq("id", eventId)
    .maybeSingle();
  if (ev?.organizer_id === userId) return "organizer";

  const { data: staffRow } = await supabaseAdmin
    .from("event_staff")
    .select("role")
    .eq("event_id", eventId)
    .eq("user_id", userId)
    .maybeSingle();
  if (staffRow?.role === "scanner" || staffRow?.role === "manager") return staffRow.role;

  return null;
}

/**
 * Append-only logger. Tolerant: failures are logged server-side but never
 * break the validation flow (which has already mutated ticket state).
 */
async function logTicketValidation(
  supabaseAdmin: any,
  payload: {
    ticketId?: string | null;
    eventId?: string | null;
    validatedByUserId: string | null;
    validatedByRole: string | null;
    validationResult: "valid" | "used" | "invalid" | "forbidden";
    message?: string | null;
    qrData?: string | null;
    ticketStatusBefore?: string | null;
    ticketStatusAfter?: string | null;
    ticketLabel?: string | null;
  }
): Promise<void> {
  try {
    const { error } = await supabaseAdmin.from("ticket_validations").insert({
      ticket_id: payload.ticketId ?? null,
      event_id: payload.eventId ?? null,
      validated_by_user_id: payload.validatedByUserId,
      validated_by_role: payload.validatedByRole,
      validation_result: payload.validationResult,
      message: payload.message ?? null,
      qr_data: payload.qrData ?? null,
      ticket_status_before: payload.ticketStatusBefore ?? null,
      ticket_status_after: payload.ticketStatusAfter ?? null,
      ticket_label: payload.ticketLabel ?? null,
    });
    if (error) console.error("[ticket_validations] insert failed:", error.message);
  } catch (err) {
    console.error("[ticket_validations] unexpected logger error:", err);
  }
}

export const validateTicket = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(z.object({ qrData: z.string().min(1).max(500) }))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId } = context;

    // Find ticket by QR (try exact match first, fallback to LIKE for legacy formats)
    const { data: exactTicket } = await supabaseAdmin
      .from("tickets")
      .select(`
        id, status, event_id, user_id, qr_data, price, ticket_label, inventory_type,
        events(title, date, location, city),
        profiles:user_id(display_name)
      `)
      .eq("qr_data", data.qrData)
      .maybeSingle();

    let foundTicket: any = exactTicket;
    if (!foundTicket) {
      const { data: ticket } = await supabaseAdmin
        .from("tickets")
        .select("id, status, event_id, user_id, qr_data, ticket_label, inventory_type, events(title)")
        .like(
          "qr_data",
          `%${data.qrData.includes("ZYNTRA-") ? data.qrData.split("-").slice(0, 2).join("-") : data.qrData}%`
        )
        .limit(1)
        .maybeSingle();
      foundTicket = ticket;
    }

    if (!foundTicket) {
      // Invalid QR — log without ticket/event context
      await logTicketValidation(supabaseAdmin, {
        ticketId: null,
        eventId: null,
        validatedByUserId: userId,
        validatedByRole: null,
        validationResult: "invalid",
        message: "El código no corresponde a ningún ticket registrado.",
        qrData: data.qrData,
      });
      return { status: "invalid" as const, message: "El código no corresponde a ningún ticket registrado." };
    }

    // Authorization: organizer of the event, admin, or assigned staff (scanner/manager)
    const { data: canValidate, error: canErr } = await supabaseAdmin.rpc("can_validate_event", {
      _user_id: userId,
      _event_id: foundTicket.event_id,
    });
    if (canErr) {
      throw new Response("Error verificando permisos.", { status: 500 });
    }
    if (!canValidate) {
      // Forbidden — log attempt with whatever ticket context we have.
      // Role is null here because the user has no operative role on this event.
      await logTicketValidation(supabaseAdmin, {
        ticketId: foundTicket.id,
        eventId: foundTicket.event_id,
        validatedByUserId: userId,
        validatedByRole: null,
        validationResult: "forbidden",
        message: "Intento de validación sin permisos sobre este evento.",
        qrData: data.qrData,
        ticketStatusBefore: foundTicket.status,
        ticketStatusAfter: foundTicket.status,
        ticketLabel: foundTicket.ticket_label ?? null,
      });
      throw new Response("No tenés permisos para validar tickets de este evento.", { status: 403 });
    }

    const validatorRole = await resolveValidatorRole(supabaseAdmin, userId, foundTicket.event_id);
    const seatLabel = foundTicket.ticket_label || undefined;
    const inventoryType = foundTicket.inventory_type || undefined;
    const eventTitle = foundTicket.events?.title;

    if (foundTicket.status === "used") {
      await logTicketValidation(supabaseAdmin, {
        ticketId: foundTicket.id,
        eventId: foundTicket.event_id,
        validatedByUserId: userId,
        validatedByRole: validatorRole,
        validationResult: "used",
        message: "Intento sobre ticket ya utilizado.",
        qrData: data.qrData,
        ticketStatusBefore: "used",
        ticketStatusAfter: "used",
        ticketLabel: foundTicket.ticket_label ?? null,
      });
      return {
        status: "used" as const,
        message: "Este ticket ya fue utilizado.",
        ticketId: foundTicket.id,
        eventTitle,
        seatLabel,
        inventoryType,
      };
    }

    if (foundTicket.status === "active") {
      // Mark as used
      await supabaseAdmin
        .from("tickets")
        .update({ status: "used", used_at: new Date().toISOString() })
        .eq("id", foundTicket.id);

      await logTicketValidation(supabaseAdmin, {
        ticketId: foundTicket.id,
        eventId: foundTicket.event_id,
        validatedByUserId: userId,
        validatedByRole: validatorRole,
        validationResult: "valid",
        message: "Ticket validado correctamente.",
        qrData: data.qrData,
        ticketStatusBefore: "active",
        ticketStatusAfter: "used",
        ticketLabel: foundTicket.ticket_label ?? null,
      });

      return {
        status: "valid" as const,
        message: "Ticket válido. Acceso permitido.",
        ticketId: foundTicket.id,
        eventTitle,
        seatLabel,
        inventoryType,
      };
    }

    // Other statuses (cancelled, refunded, etc.) → invalid
    await logTicketValidation(supabaseAdmin, {
      ticketId: foundTicket.id,
      eventId: foundTicket.event_id,
      validatedByUserId: userId,
      validatedByRole: validatorRole,
      validationResult: "invalid",
      message: `Ticket en estado "${foundTicket.status}".`,
      qrData: data.qrData,
      ticketStatusBefore: foundTicket.status,
      ticketStatusAfter: foundTicket.status,
      ticketLabel: foundTicket.ticket_label ?? null,
    });

    return {
      status: "invalid" as const,
      message: `Ticket en estado "${foundTicket.status}". No se permite el ingreso.`,
      ticketId: foundTicket.id,
      seatLabel,
      inventoryType,
    };
  });
